import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type NavigateOptions = {
  scroll?: boolean;
};

type NavigateHandler = (to: string, options?: NavigateOptions) => void;

type Router = {
  push: NavigateHandler;
  replace: NavigateHandler;
  back: () => void;
  forward: () => void;
  refresh: () => void;
  prefetch: (to: string) => void;
};

/**
 * Router hook providing navigation methods.
 * Wraps React Router's useNavigate with a cleaner API.
 */
export function useRouter(): Router {
  const navigate = useNavigate();

  const push = useCallback<NavigateHandler>(
    (to, _options) => {
      navigate(to);
    },
    [navigate]
  );

  const replace = useCallback<NavigateHandler>(
    (to, _options) => {
      navigate(to, { replace: true });
    },
    [navigate]
  );

  const back = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const forward = useCallback(() => {
    navigate(1);
  }, [navigate]);

  const refresh = useCallback(() => {
    window.location.reload();
  }, []);

  const prefetch = useCallback((_to: string) => {
    // No-op in SPA - React Router doesn't support prefetching
  }, []);

  return useMemo(
    () => ({ push, replace, back, forward, refresh, prefetch }),
    [push, replace, back, forward, refresh, prefetch]
  );
}

/**
 * Returns the current location object.
 * Use this when you need to react to any location change.
 */
export { useLocation };

/**
 * Returns the current pathname.
 */
export function usePathname() {
  const location = useLocation();
  return location.pathname;
}

/**
 * Returns the current URL search params.
 * Derives params from useLocation to ensure reactivity on URL changes.
 */
export function useSearchParams() {
  const location = useLocation();
  return useMemo(() => new URLSearchParams(location.search), [location.search]);
}
